import React, { useRef, useEffect, useCallback } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'

import CONFIG from '~/config'
import { EXPERIMENTS } from '~/constants/experiments'
import { isReCaptchaV3Enabled } from '~/features/ReCaptcha/utils'
import { useExperiment } from '~/hooks/useExperiment'

const RECAPTCHA_V2 = 'v2'

export const useReCAPTCHAv2 = (error, reset, setError) => {
  const recaptchaRef = useRef()
  const { enabled: skipRecaptcha, payload: skipRecaptchaToken } = useExperiment(EXPERIMENTS.SKIP_RECAPTCHA, false)

  // if recaptcha experiment is turned off - we set some payload to pass validation
  useEffect(() => {
    if (skipRecaptcha) {
      reset(formValues => ({
        ...formValues,
        recaptchaToken: skipRecaptchaToken,
        recaptchaVersion: RECAPTCHA_V2,
      }))
    }
  }, [reset, skipRecaptcha, skipRecaptchaToken])

  // fires on success, also gets called when expired (with null param instead of token)
  const handleRecaptchaChange = useCallback(token => {
    reset(formValues => ({
      ...formValues,
      recaptchaToken: token,
      recaptchaVersion: RECAPTCHA_V2,
    }))
  }, [reset])

  const handleRecaptchaErrored = useCallback(() => {
    setError('recaptchaToken', {
      type: 'custom',
      message: 'Sorry, there was an error processing the form, please refresh the page and try again.',
    })
  }, [setError])

  const handleRecaptchaReset = useCallback(() => {
    if (!skipRecaptcha || !isReCaptchaV3Enabled) {
      recaptchaRef.current?.reset()
      handleRecaptchaChange('')
    }
  }, [handleRecaptchaChange, skipRecaptcha])

  const ReCAPTCHAComponent = skipRecaptcha || isReCaptchaV3Enabled ? null : (
    <>
      <div className="flex justify-center mb-2.5">
        <ReCAPTCHA
          sitekey={CONFIG.RECAPTCHA_SITE_KEY}
          size="normal"
          onChange={handleRecaptchaChange}
          onErrored={handleRecaptchaErrored}
          ref={recaptchaRef}
        />
      </div>
      {error ? (
        <p className="text-red text-sm mb-2.5">
          {error.message}
        </p>
      ) : null}
    </>
  )

  return {
    ReCAPTCHA: ReCAPTCHAComponent,
    onRecaptchaReset: handleRecaptchaReset,
  }
}
